/**
 * The user's own settings — language, appearance, text size, default schedule view, density, and phone
 * numbers on Today, and the Ask button. The gear shows the first three (1.5.16: Language · Appearance · Text size ·
 * My settings · Admin portal · Help & Support · Sign out — src/app/GearMenu.tsx); My settings (/settings) shows them
 * all. Nothing from this list appears anywhere else in the header.
 *
 * 1.5.7: "Hide my number when I call from the app" is here too (/settings). It is kept on the person's account,
 * not only this device (src/lib/calling.ts), and "Call through Teams" is a third choice only while the
 * administrator has "Teams Phone is set up" on.
 */
import { useTranslation } from 'react-i18next';
import { usePrefs, type Density, type Lang, type ScheduleView, type TextSize, type Theme } from '@/lib/prefs';
import type { CallMode } from '@shared/calling';
import { useCalling, useSetCallMode } from '@/lib/calling';
import { useAnnounce } from './Announcer';

function Seg<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="row" role="group" aria-label={label}>
      <span aria-hidden="true">{label}</span>
      <div className="seg">
        {options.map((o) => (
          <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The person's own calling choice: a switch, or three choices while Teams Phone is set up. */
function CallingSetting() {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const calling = useCalling();
  const save = useSetCallMode();
  const choose = async (mode: CallMode) => {
    const out = await save(mode);
    if (out.saved === 'refused') announce(t('settings.callingRefused', { why: out.why }), 'assertive');
    else announce(t(out.saved === 'account' ? 'settings.saved' : 'settings.callingSavedDevice'));
  };
  return (
    <>
      <div data-testid="set-calling">
        {calling.teams ? (
          <Seg<CallMode>
            label={t('settings.callingTeams')}
            value={calling.mode}
            options={[
              { value: 'normal', label: t('settings.callNormal') },
              { value: 'hide', label: t('settings.callHide') },
              { value: 'teams', label: t('settings.callTeams') },
            ]}
            onChange={(mode) => void choose(mode)}
          />
        ) : (
          <Seg<CallMode>
            label={t('settings.calling')}
            value={calling.mode}
            options={[
              { value: 'hide', label: t('settings.callOn') },
              { value: 'normal', label: t('settings.callOff') },
            ]}
            onChange={(mode) => void choose(mode)}
          />
        )}
      </div>
      <p className="help">
        {t('settings.callingHelp')}
        {calling.teams ? ` ${t('settings.callingHelpTeams')}` : ''}
      </p>
    </>
  );
}

export function SettingsControls({ gear = false }: { gear?: boolean } = {}) {
  const { t } = useTranslation();
  const { prefs, setPrefs } = usePrefs();
  const announce = useAnnounce();
  const set = (patch: Parameters<typeof setPrefs>[0]) => {
    setPrefs(patch);
    announce(t('settings.saved'));
  };
  return (
    <>
      <Seg<Lang>
        label={t('settings.language')}
        value={prefs.lang}
        options={[
          { value: 'en', label: t('settings.english') },
          { value: 'es', label: t('settings.spanish') },
        ]}
        onChange={(lang) => set({ lang })}
      />
      <Seg<Theme>
        label={t('settings.appearance')}
        value={prefs.theme}
        options={[
          { value: 'light', label: t('settings.regular') },
          { value: 'dark', label: t('settings.dark') },
          { value: 'auto', label: t('settings.auto') },
        ]}
        onChange={(theme) => set({ theme })}
      />
      <Seg<TextSize>
        label={t('settings.textSize')}
        value={prefs.size}
        options={[
          { value: 'md', label: t('settings.standard') },
          { value: 'lg', label: t('settings.larger') },
        ]}
        onChange={(size) => set({ size })}
      />
      {gear ? null : (
        <>
      {/* 1.5.16: with the three above, this one follows the person; the four under it are this device's own */}
      <CallingSetting />
      <Seg<ScheduleView>
        label={t('settings.view')}
        value={prefs.view}
        options={[
          { value: 'list', label: t('settings.viewList') },
          { value: 'calendar', label: t('settings.viewCalendar') },
        ]}
        onChange={(view) => set({ view })}
      />
      <Seg<Density>
        label={t('settings.density')}
        value={prefs.density}
        options={[
          { value: 'comfortable', label: t('settings.comfortable') },
          { value: 'compact', label: t('settings.compact') },
        ]}
        onChange={(density) => set({ density })}
      />
      <Seg<'on' | 'off'>
        label={t('settings.showPhones')}
        value={prefs.showPhones ? 'on' : 'off'}
        options={[
          { value: 'on', label: t('settings.on') },
          { value: 'off', label: t('settings.off') },
        ]}
        onChange={(v) => set({ showPhones: v === 'on' })}
      />
      <Seg<'on' | 'off'>
        label={t('settings.showAssistant')}
        value={prefs.hideAssistant ? 'off' : 'on'}
        options={[
          { value: 'on', label: t('settings.on') },
          { value: 'off', label: t('settings.off') },
        ]}
        onChange={(v) => set({ hideAssistant: v === 'off' })}
      />
        </>
      )}
    </>
  );
}
